// A library shared by link (issue 1171): the owner's "Share…" dialog, and the "Shared with you" section of the Library.
//
// The dialog makes a share from the person's own pictures and sounds, names people by handle, and gives the link to
// copy; it lists the person's shares with who was named, who has opened the link, Revoke, Add people and Delete. A
// deleted share deletes no file. "Shared with you" lists what was granted to this person: its pictures are placed and
// made the map, its sounds played, exactly as their own — the server checks the share on every one of those.
import { useEffect, useState } from 'react';
import { SHARE_LIMITS, type AssetInfo, type AudioInfo, type LibraryShare, type ReceivedShare, type ShareItemRef } from '@dragons-table/vtt-shared';
import { Modal } from '../Dialogs';
import { listSounds } from '../../sound';
import { createShare, deleteShare, listMyShares, listSharedWithMe, revokeMember, shareLink, updateShare, type ShareResult } from '../../shares';
import { BTN, PRIMARY } from '../ui';

const btn = BTN;
const primary = PRIMARY;

/** The handles typed, split on commas and spaces, a leading @ dropped. */
export const handlesOf = (line: string): string[] => [...new Set(line.split(/[\s,]+/).map((h) => h.replace(/^@/, '').trim()).filter(Boolean))];

const said = (r: ShareResult): string | null => (r.kind === 'ok' ? null
  : r.kind === 'unknown_handle' ? `Nobody has the handle "${r.handle}". Check the spelling.`
    : r.kind === 'refused' ? r.reason : 'That could not be saved right now.');

export function ShareDialog({ pictures, onClose }: { pictures: AssetInfo[]; onClose: () => void }) {
  const [tracks, setTracks] = useState<AudioInfo[]>([]);
  const [shares, setShares] = useState<LibraryShare[] | null>(null);
  const [name, setName] = useState('');
  const [picked, setPicked] = useState<ShareItemRef[]>([]);
  const [people, setPeople] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void listSounds().then((r) => setTracks(r?.tracks ?? []));
    void listMyShares().then((s) => setShares(s ?? []));
  }, []);
  const isPicked = (i: ShareItemRef) => picked.some((p) => p.kind === i.kind && p.id === i.id);
  const toggle = (i: ShareItemRef, on: boolean) => setPicked((cur) => (on ? [...cur, i].slice(0, SHARE_LIMITS.items) : cur.filter((p) => !(p.kind === i.kind && p.id === i.id))));
  const replace = (s: LibraryShare) => setShares((cur) => (cur ?? []).map((x) => (x.id === s.id ? s : x)));
  const nameOf = (i: ShareItemRef) => (i.kind === 'picture' ? pictures.find((p) => p.id === i.id)?.name : tracks.find((t) => t.id === i.id)?.name) ?? 'a file';

  const create = async () => {
    setBusy(true); setMessage('');
    const r = await createShare({ name: name.trim(), items: picked, handles: handlesOf(people) });
    setBusy(false);
    if (r.kind === 'ok') {
      setShares((cur) => [r.share, ...(cur ?? [])]);
      setName(''); setPicked([]); setPeople('');
      setMessage(`"${r.share.name}" is shared. Send each person its link.`);
    } else setMessage(said(r)!);
  };

  return (
    <Modal title="Share from your library" onClose={onClose} testId="share-dialog">
      <div className="max-h-[70vh] space-y-3 overflow-y-auto text-sm">
        <form className="space-y-2 rounded border border-stone-700 p-2" onSubmit={(e) => { e.preventDefault(); void create(); }}>
          <label className="block text-xs text-stone-400">Name of the share
            <input aria-label="Name of the share" className="mt-1 block w-full rounded bg-stone-800 px-2 py-1 text-sm text-stone-100" maxLength={SHARE_LIMITS.name} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <fieldset className="space-y-1">
            <legend className="text-xs text-stone-400">What to share</legend>
            {pictures.length + tracks.length === 0 && <p className="text-xs text-stone-500">Nothing in your library yet.</p>}
            <ul className="max-h-40 space-y-0.5 overflow-y-auto" data-testid="share-pick">
              {pictures.map((p) => (
                <li key={`p-${p.id}`}><label className="flex items-center gap-1 text-xs"><input type="checkbox" aria-label={`Share ${p.name}`} checked={isPicked({ kind: 'picture', id: p.id })} onChange={(e) => toggle({ kind: 'picture', id: p.id }, e.target.checked)} />{p.name} <span className="text-stone-500">{p.kind}</span></label></li>
              ))}
              {tracks.map((t) => (
                <li key={`a-${t.id}`}><label className="flex items-center gap-1 text-xs"><input type="checkbox" aria-label={`Share ${t.name}`} checked={isPicked({ kind: 'audio', id: t.id })} onChange={(e) => toggle({ kind: 'audio', id: t.id }, e.target.checked)} />{t.name} <span className="text-stone-500">sound</span></label></li>
              ))}
            </ul>
          </fieldset>
          <label className="block text-xs text-stone-400">People, by handle (separate with commas)
            <input aria-label="People to share with" className="mt-1 block w-full rounded bg-stone-800 px-2 py-1 text-sm text-stone-100" placeholder="@handle, @handle" value={people} onChange={(e) => setPeople(e.target.value)} />
          </label>
          <button type="submit" className={primary} disabled={busy || !name.trim() || picked.length === 0}>Share</button>
        </form>
        {message && <p role="status" className="text-xs text-amber-200" data-testid="share-message">{message}</p>}
        <section aria-label="Your shares" className="space-y-2">
          <h3 className="vtt-section-head">Your shares</h3>
          {shares === null && <p className="text-xs text-stone-500">Loading…</p>}
          {shares?.length === 0 && <p className="text-xs text-stone-500">You have not shared anything.</p>}
          {(shares ?? []).map((s) => (
            <OwnShare key={s.id} share={s} nameOf={nameOf} onChanged={replace} onSaid={setMessage}
              onDeleted={() => { setShares((cur) => (cur ?? []).filter((x) => x.id !== s.id)); setMessage(`"${s.name}" is no longer shared. Your files are kept.`); }} />
          ))}
        </section>
        <div className="flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </div>
    </Modal>
  );
}

function OwnShare({ share, nameOf, onChanged, onDeleted, onSaid }: { share: LibraryShare; nameOf: (i: ShareItemRef) => string; onChanged: (s: LibraryShare) => void; onDeleted: () => void; onSaid: (m: string) => void }) {
  const [more, setMore] = useState('');
  const link = shareLink(share.token);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); onSaid('The link is copied.'); } catch { onSaid('Copy the link from the box.'); }
  };
  return (
    <div className="space-y-1 rounded border border-stone-800 p-2 text-xs" data-testid="own-share" data-name={share.name}>
      <div className="font-medium">{share.name} <span className="text-stone-500">· {share.items.length} {share.items.length === 1 ? 'file' : 'files'}</span></div>
      <p className="text-stone-400">{share.items.map(nameOf).join(', ')}</p>
      <div className="flex items-center gap-1">
        <input readOnly aria-label={`Link to ${share.name}`} data-testid="share-link" className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-0.5" value={link} onFocus={(e) => e.target.select()} />
        <button type="button" className={btn} onClick={() => { void copy(); }}>Copy link</button>
      </div>
      <ul className="space-y-0.5" data-testid="share-members">
        {share.members.map((m) => (
          <li key={m.userId} className="flex items-center gap-1" data-testid="share-member" data-handle={m.handle ?? ''}>
            <span>{m.handle ? `@${m.handle}` : m.displayName ?? 'Someone'}</span>
            <span className="text-stone-500">{m.revokedAt ? 'revoked' : m.grantedAt ? 'has it' : 'not opened yet'}</span>
            {!m.revokedAt && (
              <button type="button" className={`${btn} ml-auto`} aria-label={`Revoke ${m.handle ?? 'this person'}`}
                onClick={async () => { const r = await revokeMember(share.id, m.userId); if (r.kind === 'ok') onChanged(r.share); else onSaid(said(r)!); }}>Revoke</button>
            )}
          </li>
        ))}
      </ul>
      <form className="flex items-center gap-1" onSubmit={async (e) => {
        e.preventDefault();
        const handles = handlesOf(more);
        if (handles.length === 0) return;
        const r = await updateShare(share.id, { handles });
        if (r.kind === 'ok') { onChanged(r.share); setMore(''); } else onSaid(said(r)!);
      }}>
        <input aria-label={`Add people to ${share.name}`} className="min-w-0 flex-1 rounded bg-stone-800 px-1 py-0.5" placeholder="@handle" value={more} onChange={(e) => setMore(e.target.value)} />
        <button type="submit" className={btn}>Add</button>
        <button type="button" className={btn} aria-label={`Delete the share ${share.name}`} onClick={async () => { if (await deleteShare(share.id)) onDeleted(); else onSaid('That share could not be deleted right now.'); }}>Delete share</button>
      </form>
    </div>
  );
}

/** "Shared with you": each granted share, its pictures with the Library's own actions and its sounds with Play. */
export function SharedWithYou({ actions, onPlay, refreshKey = 0 }: {
  actions: (a: AssetInfo) => React.ReactNode;
  onPlay: (t: AudioInfo, channel: 'music' | 'ambience' | 'effect') => void;
  refreshKey?: number;
}) {
  const [shares, setShares] = useState<ReceivedShare[] | null>(null);
  useEffect(() => { void listSharedWithMe().then(setShares); }, [refreshKey]);
  if (!shares || shares.length === 0) return null;
  return (
    <section aria-label="Shared with you" className="space-y-2" data-testid="shared-with-you">
      <h3 className="vtt-section-head">Shared with you</h3>
      {shares.map((s) => (
        <div key={s.id} className="rounded border border-stone-800 p-2" data-testid="received-share" data-name={s.name}>
          <div className="text-xs"><span className="font-medium">{s.name}</span> <span className="text-stone-500">from {s.owner.handle ? `@${s.owner.handle}` : s.owner.displayName ?? 'someone'}</span></div>
          <ul className="divide-y divide-stone-800">
            {s.pictures.map((a) => (
              <li key={a.id} className="space-y-1 py-1" data-testid={`shared-asset-${a.id}`} data-name={a.name}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-stone-800 px-1.5 text-xs uppercase tracking-wide text-stone-400">{a.kind}</span>
                  <span className="font-medium">{a.name}</span>
                  <span className="text-xs text-stone-500">{a.width} × {a.height}</span>
                </div>
                <div className="flex flex-wrap gap-1">{actions(a)}</div>
              </li>
            ))}
            {s.tracks.map((t) => (
              <li key={t.id} className="space-y-1 py-1 text-xs" data-testid={`shared-track-${t.id}`} data-name={t.name}>
                <span className="font-medium">{t.name}</span> <span className="text-stone-500">sound</span>
                <div className="flex flex-wrap gap-1">
                  <button type="button" className={btn} onClick={() => onPlay(t, 'music')}>Play as music</button>
                  <button type="button" className={btn} onClick={() => onPlay(t, 'ambience')}>Loop as ambience</button>
                  <button type="button" className={btn} onClick={() => onPlay(t, 'effect')}>Play as effect</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
